/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2024, Zumper
 * @description MarketingSection
 */

import { Link } from '@chakra-ui/react'
import { Flex, Heading, Image, Text } from '@chakra-ui/react'
import { selectIsMobile } from '@zcode-ui/web-zumper-global-state'
import {
  LIST_YOUR_PROPERTY_CLICK,
  LIST_YOUR_PROPERTY_IMPRESSION,
  event,
} from '@zcode-ui/web-zumper-integrations'
import { EVENT_NAMES, HOMEPAGE, PAGE_NAMES, PROP_NAMES, track } from '@zcode/mixpanel'
import { Origin } from '@zcode/nr-utils'
import { routesConstants } from '@zcode/zumper-routes'
import * as React from 'react'
import { useDispatch } from 'react-redux'
import { useSelector } from 'react-redux'
import { useHomepageImpression } from '../hooks/useHomepageImpression'
import marketIcon from './svg/marketIcon.svg'
import marketIconMobile from './svg/marketIconMweb.svg'

const MarketingSection = () => {
  const isMobile = useSelector(selectIsMobile)

  const ref = useHomepageImpression({
    name: LIST_YOUR_PROPERTY_IMPRESSION,
    mixpanelSection: HOMEPAGE.LIST_YOUR_PROEPRTY,
  })
  const dispatch = useDispatch()
  const onClickListProperty = () => {
    dispatch(event({ name: LIST_YOUR_PROPERTY_CLICK, props: { origin: Origin.HOMEPAGE } }))
    track({
      event: EVENT_NAMES.CLICK,
      payload: {
        [PROP_NAMES.PAGE_NAME]: PAGE_NAMES.HOMEPAGE,
        [PROP_NAMES.BUTTON_CLICKED]: 'List Your Property',
      },
    })
  }

  return (
    <Flex
      ref={ref}
      background="gray.50"
      border={{ base: 'none', desktop: '1px solid var(--chakra-colors-gray-150)' }}
      justifyContent="space-between"
      h={{ desktop: '400px' }}
      flexDir={{ base: 'column', desktop: 'row' }}
      width={{ base: 'auto', desktop: 'full' }}
      p={{ base: '40px 24px', desktop: 0 }}
      pt={{ desktop: 12, lg: '54px', xl: '88px' }}
      borderRadius={{ base: 'none', desktop: '3xl' }}
      mx={{ base: '-24px', desktop: 0 }}
    >
      {isMobile && (
        <Image
          alt=""
          display={{ base: 'block', desktop: 'none' }}
          loading="eager"
          mt="auto"
          src={marketIconMobile}
          mb={8}
        />
      )}
      <Flex
        flexDir="column"
        flexShrink={0}
        mr={{ desktop: 6, lg: 0, xl: 12 }}
        ml={{ desktop: 6, lg: 8, xl: 12 }}
        w={{ desktop: '257px', lg: '337px', xl: '492px' }}
      >
        <Heading mb={{ base: 4, desktop: 2 }} variant="display28To32">
          Marketing solutions for our partners big and small
        </Heading>
        <Text color="gray.950" variant="body16Reg" mb={{ base: 10, desktop: 6, lg: 12 }}>
          Whether you're a property owner, manager, or broker, we offer customized solutions to help
          you reach millions of quality renters during every step of the renter journey.
        </Text>
        <Link
          _hover={{
            background: 'gray.50',
            borderColor: 'gray.800',
          }}
          height="40px"
          background="gray.0"
          border="1px solid var(--chakra-colors-gray-350)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          textDecoration="none"
          fontSize="md"
          borderRadius="md"
          fontWeight="bold"
          w="168px"
          href={routesConstants.ADVERTISE_URL}
          onClick={onClickListProperty}
        >
          List your property
        </Link>
      </Flex>
      {!isMobile && (
        <Image
          alt=""
          display={{ base: 'none', desktop: 'block' }}
          loading="eager"
          mt="auto"
          src={marketIcon}
        />
      )}
    </Flex>
  )
}

export default MarketingSection
